import React, { useContext, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import {
  Collapse,
  useCollapseState,
  ValidatedBootstrapPanel,
  ValidatedPanel,
  ValidatedFixedPanel,
  TextTitle,
} from '@bingads-webui-campaign/wizard-jumbo-card';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { Panel as BootstrapPanel } from 'react-bootstrap';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { CampaignSubTypes } from '@bingads-webui-campaign-react/constants';
import {
  ValidatedAudiencePicker,
  formatAudience,
} from '@bingads-webui-campaign-react/xandr-campaign';
import { MessageBarType, MessageBar } from '@fluentui/react';
import { ServerContext } from '@bingads-webui-campaign-react/server-context';
import { ValidatedCompanyPicker, formatCompany } from '@bingads-webui-campaign-react/display-plus-campaigns/src/steps/adgroup/components/company-target';
import { ValidatedIndustryPicker, formatIndustry } from '@bingads-webui-campaign-react/display-plus-campaigns/src/steps/adgroup/components/industry-target';
import { ValidatedJobFunctionPicker, formatJobFunction } from '@bingads-webui-campaign-react/display-plus-campaigns/src/steps/adgroup/components/job-function-target';
import { SectionShimmer, PredictiveTargetingControlHeader, ERROR_ENTITY } from '@bingads-webui-campaign-react/display-plus-campaigns';
import { withControlErrorHandling, withUnvalidatedControlErrors } from '@bingads-webui-campaign-react/error-handling';
import { andComp, entityIs, propertyIs } from '@bingads-webui-campaign-react/form-error';
import { getGridTargetTitleDetails } from '@bingads-webui-campaign/hotel-common-upsert';
import { DeviceImg } from '@bingads-webui-campaign-react/sponsored-campaign-creation';

import { getAdGroupStyles, getTargetingPanels } from '../utils';
import { isSearchShoppingCampaign } from '../../step-utils';
import { DeviceTargets as ValidatedTargetDevices, convertToGridTargetDetails } from './device-target';

const getPanelErrorProps = ({ needsAttention }) => (needsAttention ? {
  error: _TL_("* Something's wrong"),
} : {});

const ServerValidatedTargetDevice = withControlErrorHandling(withUnvalidatedControlErrors(ValidatedTargetDevices, {
  filter: andComp(entityIs(ERROR_ENTITY.Campaign), propertyIs('device')),
}));

const TargetingPreferencesInternal = ({
  data,
  flow,
}) => {
  const i18n = useContext(I18nContext);
  const serverContext = useContext(ServerContext);
  const isAudienceCampaing = !isSearchShoppingCampaign(flow);

  const {
    campaign,
    adGroup,
  } = data || {};

  const [optimizedTargetingEnabled, setOptimizedTargetingEnabled] = useState(false);

  const { advancedOptions } = getTargetingPanels(flow, serverContext);

  const {
    devices: campaignDeviceTargets,
    audiences: campaignAudiences,
    industry: campaignIndustry,
    negativeIndustry: campaignNegativeIndustry,
    jobTargets: campaignJobTargets,
    negativeJobTargets: campaignNegativeJobTargets,
    companyNameTargets: campaignCompanyNameTargets,
    negativeCompanyNameTargets: campaignNegativeCompanyNameTargets,
    BiddingScheme: biddingScheme,
  } = campaign || {};
  const { Type: bidStrategyType } = biddingScheme || {};

  const {
    audiences: adGroupAudiences,
    industry: adGroupIndustry,
    negativeIndustry: adGroupNegativeIndustry,
    jobTargets: adGroupJobTargets,
    negativeJobTargets: adGroupNegativeJobTargets,
    companyNameTargets: adGroupCompanyNameTargets,
    negativeCompanyNameTargets: adGroupNegativeCompanyNameTargets,
  } = adGroup || {};

  const audiences = isAudienceCampaing ? adGroupAudiences : campaignAudiences;
  const industry = isAudienceCampaing ? adGroupIndustry : campaignIndustry;
  const negativeIndustry = isAudienceCampaing ? adGroupNegativeIndustry : campaignNegativeIndustry;
  const jobTargets = isAudienceCampaing ? adGroupJobTargets : campaignJobTargets;
  const negativeJobTargets = isAudienceCampaing ? adGroupNegativeJobTargets : campaignNegativeJobTargets;
  const companyNameTargets = isAudienceCampaing ? adGroupCompanyNameTargets : campaignCompanyNameTargets;
  const negativeCompanyNameTargets = isAudienceCampaing ? adGroupNegativeCompanyNameTargets : campaignNegativeCompanyNameTargets;

  const classes = useDefaultStyles(getAdGroupStyles);
  const [
    allAdGroupTargetingSections,
    adgroupTargetingExpandedSections,
    setAdGroupTargetingExpandedSections,
  ] = useCollapseState(
    advancedOptions /* all sections */,
    [] /* initially expanded sections */
  );

  const getCampaignSubType = useMemo(() => {
    if (isAudienceCampaing) {
      return CampaignSubTypes.AudienceFeedBased;
    }
    return null;
  }, [isAudienceCampaing]);

  const adGroupTargetingSections = () => (
    <Collapse allPanelItems={allAdGroupTargetingSections} checkItems skipItemsTypeCheck activeKeys={adgroupTargetingExpandedSections} setActiveKeys={setAdGroupTargetingExpandedSections}>
      <ValidatedPanel
        key="audience-target"
        title={<TextTitle
          titleKey={_TL_('Audience')}
          key="audience-target"
          panelKey="audience-target"
          details={formatAudience({ audiences, i18n })}
        />}
      >
        {
          audiences ? (
            <ValidatedAudiencePicker
              i18n={i18n}
              campaignSubType={getCampaignSubType}
              supportExclusion={isAudienceCampaing}
              validationProps={{
                data: {
                  audiences,
                },
              }}
            />
          ) : (<SectionShimmer />)
        }
      </ValidatedPanel>
      <ValidatedPanel
        key="device-target"
        title={<TextTitle
          titleKey={_TL_('Device')}
          key="device-target"
          panelKey="device-target"
          icon={DeviceImg}
          details={getGridTargetTitleDetails(i18n, convertToGridTargetDetails(campaignDeviceTargets))}
        />}
      >
        {
          campaignDeviceTargets ? (
            <ServerValidatedTargetDevice
              bidStrategyType={bidStrategyType}
              validationProps={{
                data: campaignDeviceTargets,
              }}
            />
          ) : (<SectionShimmer />)
        }
      </ValidatedPanel>
      <ValidatedPanel
        key="company-target"
        title={<TextTitle
          titleKey={_TL_('Company')}
          key="company-target"
          panelKey="company-target"
          topicKey="pop_BA_CampaignCreate_AudTargets_Company"
          details={formatCompany({ companyNameTargets, i18n })}
        />}
      >
        <ValidatedCompanyPicker
          classes={classes}
          campaignSubType={CampaignSubTypes.BrandAwarenessVideoAds}
          validationProps={{
            data: {
              companyNameTargets,
              negativeCompanyNameTargets,
            },
          }}
        />
      </ValidatedPanel>
      <ValidatedPanel
        key="industry-target"
        title={<TextTitle
          titleKey={_TL_('Industry')}
          key="industry-target"
          panelKey="industry-target"
          validationProps={{
            data: {
              industry,
              negativeIndustry,
            },
          }}
          details={formatIndustry({ industry, i18n })}
        />}
      >
        <ValidatedIndustryPicker classes={classes} campaignSubType={CampaignSubTypes.BrandAwarenessVideoAds} />
      </ValidatedPanel>
      <ValidatedPanel
        key="job-target"
        title={<TextTitle
          titleKey={_TL_('Job function')}
          key="job-target"
          topicKey="pop_BA_CampaignCreate_AudTargets_JobFunction"
          panelKey="job-target"
          details={formatJobFunction({ jobTargets, i18n })}
        />}
      >
        <ValidatedJobFunctionPicker
          classes={classes}
          campaignSubType={CampaignSubTypes.BrandAwarenessVideoAds}
          validationProps={{
            data: {
              jobTargets,
              negativeJobTargets,
            },
          }}
        />
      </ValidatedPanel>
      <ValidatedFixedPanel
        key="predictive-targeting-control"
        title={data ? (
          <PredictiveTargetingControlHeader
            validationProps={{
              data: adGroup,
            }}
            onToggleEnabledChanged={setOptimizedTargetingEnabled}
          />)
          : <SectionShimmer count={1} />
        }
        attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
      >
        <MessageBar
          messageBarType={MessageBarType.warning}
          isMultiline
        >
          {!optimizedTargetingEnabled ?
            i18n.getString(_TL_('Predictive targeting is automatically enabled. You can disable it by setting a targeting preference.'))
            : i18n.getString(_TL_('Turning off predictive targeting significantly limits our ability to help you reach the best audience for your campaign.'))
          }
        </MessageBar>
      </ValidatedFixedPanel>
    </Collapse>
  );

  return (
    <>
      <ValidatedBootstrapPanel
        defaultExpanded={false}
        className={classNames(classes.collapsiblePanel, 'targeting-preferences-panel')}
        render={() => (
          <>
            <BootstrapPanel.Heading>
              <BootstrapPanel.Title toggle>
                {i18n.getString(_TL_('Targeting preferences (optional)'))}
              </BootstrapPanel.Title>
            </BootstrapPanel.Heading>
            <BootstrapPanel.Collapse>
              <BootstrapPanel.Body>
                <div className={classNames('targeting-preferences-section')}>
                  {adGroupTargetingSections()}
                </div>
              </BootstrapPanel.Body>
            </BootstrapPanel.Collapse>
          </>
        )}
      />
    </>
  );
};

TargetingPreferencesInternal.propTypes = {
  data: PropTypes.shape({
    locationTargets: PropTypes.shape({}),
    adGroup: PropTypes.shape({
      locationTargets: PropTypes.shape({}),
      deviceTypes: PropTypes.shape({}),
    }),
  }).isRequired,
};

export const TargetingPreferences = TargetingPreferencesInternal;
